import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { axiosNewInstance } from "../../config/Api";
import toast from "react-hot-toast";

export const PermissionForm = ({
  editRole,
  setEditRole,
  showForm,
  setShowForm,
}) => {
  const [permissionData, setPermissionData] = useState({
    modules: [],
    permissions: [],
  });
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (Object.keys(editRole).length > 0) {
        setLoading(true);
        try {
          const token = localStorage.getItem("authToken");
          const response = await axiosNewInstance.post(
            "/api-app/permissions",
            { role_id: editRole?.id || "1" },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (response?.data?.status === 1) {
            setPermissionData(response?.data?.data);
            setSelectedPermissions(
              new Set(response?.data?.data?.permissions || [])
            );
          } else {
            toast.error(response?.data?.message);
          }
        } catch (error) {
          toast.error("Failed to fetch data. Please try again later.");
        }
        setLoading(false);
      }
    };
    fetchPermissions();
  }, [editRole]);

  // Check if all permissions are selected
  const isAllSelected = permissionData.modules.every((module) =>
    module.permissions.every((perm) => selectedPermissions.has(perm.id))
  );

  // Handle "Select All" checkbox
  const handleSelectAllChange = () => {
    if (isAllSelected) {
      setSelectedPermissions(new Set());
    } else {
      const allPermissions = new Set();
      permissionData.modules.forEach((module) => {
        module.permissions.forEach((perm) => allPermissions.add(perm.id));
      });
      setSelectedPermissions(allPermissions);
    }
  };

  // Handle individual permission checkbox change
  const handleCheckboxChange = (id) => {
    setSelectedPermissions((prev) => {
      const newPermissions = new Set(prev);
      newPermissions.has(id)
        ? newPermissions.delete(id)
        : newPermissions.add(id);
      return newPermissions;
    });
  };

  // Initialize Formik
  const formik = useFormik({
    initialValues: {
      name: editRole?.name || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Role Name is required"),
    }),
    onSubmit: async (values) => {
      setSaving(true);
      try {
        const token = localStorage.getItem("authToken");
        const response = await axiosNewInstance.post(
          "/api-app/assign-permissions",
          {
            role_id: editRole?.id || "1",
            permission_id: [...selectedPermissions],
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response?.data?.status === 1) {
          toast.success("Permissions assigned successfully!");
          setShowForm(!showForm);
          setEditRole({});
        } else {
          toast.error(response?.data?.message);
        }
      } catch (error) {
        toast.error("Failed to assign permissions. Please try again.");
      }
      setSaving(false);
    },
  });

  return (
    <div className="card shadow-sm mt-4">
      <div className="card-header bg-primary text-white">
        <h5 className="mb-0">
          {Object.keys(editRole).length > 0 &&
            "Assign Permissions for selected role"}
        </h5>
      </div>
      <div className="card-body">
        <form onSubmit={formik.handleSubmit}>
          {/* Role Name Input */}
          <div className="form-group">
            <label>Role Name</label>
            <input
              type="text"
              className="form-control"
              name="name"
              value={formik.values.name}
              disabled
              readOnly
            />
          </div>

          {/* Permissions Table */}
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="thead-light">
                <tr>
                  <th className="text-center align-middle">
                    <input
                      type="checkbox"
                      onChange={handleSelectAllChange}
                      checked={isAllSelected}
                    />
                  </th>
                  <th colSpan="3">Module</th>
                  <th className="text-center">List</th>
                  <th className="text-center">Create</th>
                  <th className="text-center">Edit</th>
                  <th className="text-center">Delete</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      Loading...
                    </td>
                  </tr>
                ) : (
                  permissionData.modules.map((module) => (
                    <tr key={module.id}>
                      <td className="text-center align-middle">
                        <input
                          type="checkbox"
                          onChange={() => {
                            const modulePermissions = module.permissions.map(
                              (perm) => perm.id
                            );
                            const hasAll = modulePermissions.every((id) =>
                              selectedPermissions.has(id)
                            );
                            setSelectedPermissions((prev) => {
                              const newSet = new Set(prev);
                              hasAll
                                ? modulePermissions.forEach((id) =>
                                    newSet.delete(id)
                                  )
                                : modulePermissions.forEach((id) =>
                                    newSet.add(id)
                                  );
                              return newSet;
                            });
                          }}
                          checked={module.permissions.every((perm) =>
                            selectedPermissions.has(perm.id)
                          )}
                        />
                      </td>
                      <td colSpan="3">{module.name}</td>
                      {["List", "Create", "Edit", "Delete"].map((action) => {
                        const permission = module.permissions.find(
                          (p) => p.name === action
                        );
                        return (
                          <td key={action} className="text-center align-middle">
                            {permission ? (
                              <input
                                type="checkbox"
                                checked={selectedPermissions.has(permission.id)}
                                onChange={() =>
                                  handleCheckboxChange(permission.id)
                                }
                              />
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Submit Button */}
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            className="btn btn-warning"
            onClick={() => {
              setShowForm(!showForm);
              setEditRole({});
            }}
            style={{ height: "45px", marginLeft: "5px" }}
          >
            Cancel
          </button>
        </form>
      </div>
    </div>
  );
};
